import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";

const TRANSITION_MS = 160;

const HandleContainer = styled("div")`
  z-index: 1;
  user-select: none;

  & [data-drag-indicator] {
    opacity: 0;
    transition: opacity 0ms;
  }

  &:hover [data-drag-indicator] {
    opacity: 1;
  }
`;

type Props = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  orientation: "vertical" | "horizontal";
  minSize?: number;
  maxSize?: number;
  minOppositeSize?: number;
  defaultSize?: React.CSSProperties["height"];
  initialSize?: number;
  overrideSize?: React.CSSProperties["height"];
  // When true, the pane settles instantly (no transition) while it is neither
  // dragged nor overridden, so a controlled pane snaps into place as its content
  // loads instead of animating in. Defaults to the animated behaviour.
  snapUndraggedResize?: boolean;
  onSizeChange?: (size: number | undefined) => void;
  Handle?: React.ReactNode;
  first: React.ReactNode;
  second: React.ReactNode;
};

export function Resizable({
  containerRef,
  orientation,
  minSize = 0,
  maxSize,
  minOppositeSize = minSize,
  Handle = "div",
  defaultSize = "50%",
  initialSize,
  overrideSize,
  snapUndraggedResize = false,
  onSizeChange,
  first,
  second,
}: Props) {
  const isDraggingRef = useRef(false);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedSize, setDraggedSize] = useState<number | undefined>(
    initialSize
  );

  // Leaving an override would otherwise snap when a dragged size exists, so
  // the transition is kept through the exit and its settle window.
  const prevOverrideRef = useRef(overrideSize);
  const exitingOverride =
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    prevOverrideRef.current !== undefined && overrideSize === undefined;
  const [settlingFromOverride, setSettlingFromOverride] = useState(false);
  useEffect(() => {
    const wasOverridden = prevOverrideRef.current !== undefined;
    prevOverrideRef.current = overrideSize;
    if (wasOverridden && overrideSize === undefined) {
      setSettlingFromOverride(true);
      const t = setTimeout(() => setSettlingFromOverride(false), TRANSITION_MS);
      return () => clearTimeout(t);
    }
  }, [overrideSize]);

  const resetSize = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    setDraggedSize(undefined);
    onSizeChange?.(undefined);
  }, [onSizeChange]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (overrideSize !== undefined) return;
      if (e.detail > 1) {
        e.preventDefault();
        resetSize();
        return;
      }

      isDraggingRef.current = true;
      setIsDragging(true);
    },
    [resetSize, overrideSize]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const handleSize =
        handleRef.current?.getBoundingClientRect()[
          orientation === "vertical" ? "height" : "width"
        ] ?? 0;
      const computedStyle = getComputedStyle(container);
      const horizontalPadding =
        parseFloat(computedStyle.paddingTop) +
        parseFloat(computedStyle.paddingBottom);
      const verticalPadding =
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);
      const newSize =
        (orientation === "vertical"
          ? rect.bottom - e.clientY
          : rect.right - e.clientX) - handleSize;
      const containerInnerSize =
        orientation === "vertical"
          ? rect.height - horizontalPadding
          : rect.width - verticalPadding;
      const upper = maxSize
        ? Math.min(maxSize, containerInnerSize - minOppositeSize - handleSize)
        : containerInnerSize - minOppositeSize - handleSize;
      const clamped = Math.max(minSize, Math.min(newSize, upper));
      setDraggedSize(clamped);
      onSizeChange?.(clamped);
    },
    [containerRef, maxSize, minSize, minOppositeSize, onSizeChange, orientation]
  );

  const size = overrideSize ?? draggedSize ?? defaultSize;

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const sizeStyle =
    orientation === "vertical" ? { height: size } : { width: size };

  const dynamicHandleStyle =
    orientation === "vertical"
      ? {
          cursor: "ns-resize",
        }
      : {
          cursor: "ew-resize",
        };

  return (
    <>
      {first}

      <HandleContainer
        ref={handleRef}
        onMouseDown={handleMouseDown}
        onDoubleClick={resetSize}
        style={{ ...dynamicHandleStyle }}
      >
        {Handle}
      </HandleContainer>

      <div
        style={{
          ...sizeStyle,
          overflow: "hidden",
          transition:
            // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
            overrideSize !== undefined ||
            // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
            exitingOverride ||
            settlingFromOverride ||
            (!snapUndraggedResize && draggedSize === undefined)
              ? `${orientation === "vertical" ? "height" : "width"} ${TRANSITION_MS}ms ease-out`
              : undefined,
        }}
      >
        {second}
      </div>

      {isDragging && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            cursor: orientation === "vertical" ? "ns-resize" : "ew-resize",
          }}
        />
      )}
    </>
  );
}
